/**
 * Collection ajax loader for wuu-tabs-product-list.
 * Fetches next pages from `collection.wuu-tabs` and appends product items.
 */

const AJAX_VIEW = 'wuu-tabs';

/**
 * @typedef {object} WuuTabsRequestContext
 * @property {AbortSignal} [signal]
 */

/**
 * Loads the next collection page into the panel grid.
 *
 * @param {HTMLElement} panel
 * @param {WuuTabsRequestContext} [ctx]
 * @returns {Promise<void>}
 */
export async function loadNext(panel, ctx = {}) {
  const collectionUrl = panel.dataset.collectionUrl;
  const nextPage = Number(panel.dataset.nextPage || '2');
  const lastPage = Number(panel.dataset.lastPage || '1');
  const grid = panel.querySelector('[data-wuu-tabs-grid]');

  if (!collectionUrl || !grid || nextPage > lastPage) {
    panel.dataset.hasMore = 'false';
    return;
  }

  const url = new URL(collectionUrl, window.location.origin);
  url.searchParams.set('view', AJAX_VIEW);
  url.searchParams.set('page', String(nextPage));

  const response = await fetch(url.toString(), {
    signal: ctx.signal,
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) throw new Error(`Failed to load products (${response.status})`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const items = doc.querySelectorAll('[data-wuu-tabs-product-item]');
  const root = doc.querySelector('[data-wuu-tabs-ajax-root]');
  const resolvedLastPage = Number(root?.getAttribute('data-last-page') || lastPage);

  if (items.length) {
    const existingIds = new Set(
      [...grid.querySelectorAll('[data-product-id]')].map((item) => item.getAttribute('data-product-id'))
    );

    for (const item of items) {
      const productId = item.getAttribute('data-product-id');
      if (productId && existingIds.has(productId)) continue;
      grid.append(item);
    }
  }

  const loadedPage = Number(root?.getAttribute('data-page') || nextPage);
  const hasMore = loadedPage < resolvedLastPage && items.length > 0;

  panel.dataset.nextPage = String(loadedPage + 1);
  panel.dataset.lastPage = String(resolvedLastPage);
  panel.dataset.hasMore = hasMore ? 'true' : 'false';
}
